(() => {
  if (window.PPSVideoViewerLoaded) return;
  window.PPSVideoViewerLoaded = true;

  const start = root => {
    if (root.querySelector('.pps-reel-open') || !window.HTMLDialogElement) return;

    const cards = [...root.querySelectorAll('.pps-reel')];
    if (!cards.length) return;

    const videos = cards.map(card => card.querySelector('video'));
    const removers = [];

    const on = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      removers.push(() => target.removeEventListener(event, handler, options));
    };

    const safePlay = video => video.play()?.catch(() => {});
    const dialog = document.createElement('dialog');

    dialog.className = 'pps-reels-viewer';
    dialog.setAttribute('aria-label', 'Inside our shop videos');

    dialog.innerHTML = `
      <div class="pps-viewer-stage">
        <div class="pps-viewer-top">
          <span class="pps-viewer-count" aria-live="polite"></span>
          <button class="pps-viewer-close" type="button" aria-label="Close video viewer" autofocus>
            <span class="pps-viewer-close-label">Close</span>
            <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5L15 15M15 5L5 15"/></svg>
          </button>
        </div>
        <div class="pps-viewer-feed" tabindex="0" aria-label="Videos. Scroll up or down to browse."></div>
        <div class="pps-viewer-bottom">
          <p class="pps-viewer-caption"></p>
          <p class="pps-viewer-status" role="status"></p>
          <div class="pps-viewer-actions">
            <button class="pps-viewer-sound" type="button"></button>
            <button class="pps-viewer-play" type="button"></button>
          </div>
        </div>
      </div>`;

    document.body.appendChild(dialog);

    const get = selector => dialog.querySelector(selector);
    const feed = get('.pps-viewer-feed');
    const play = get('.pps-viewer-play');
    const sound = get('.pps-viewer-sound');
    const status = get('.pps-viewer-status');

    const slides = cards.map((card, i) => {
      const slide = document.createElement('div');
      slide.className = 'pps-viewer-slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `Video ${i + 1} of ${cards.length}`);

      if (videos[i].poster) {
        const poster = document.createElement('img');
        poster.src = videos[i].poster;
        poster.alt = '';
        poster.loading = 'lazy';
        poster.draggable = false;

        const backdrop = poster.cloneNode(true);
        backdrop.className = 'pps-viewer-backdrop';
        slide.appendChild(backdrop);

        poster.className = 'pps-viewer-poster';
        slide.appendChild(poster);
      }

      feed.appendChild(slide);
      return slide;
    });

    let player, index = -1, trigger, previousVideo, oldOverflow;
    let open = false, muted = true, paused = false, frame = 0;

    const sync = () => {
      if (!player) return;

      play.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${player.paused
        ? '<path d="M8 5v14l11-7z"/>'
        : '<rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/>'}</svg>`;

      play.setAttribute('aria-label', player.paused ? root.dataset.play : root.dataset.pause);
      sound.textContent = muted ? 'Sound off' : 'Sound on';
      sound.setAttribute('aria-label', muted ? root.dataset.soundOn : root.dataset.soundOff);
      sound.setAttribute('aria-pressed', String(!muted));
    };

    const failed = () => {
      status.textContent = 'Video could not load. Scroll to another clip.';
    };

    const release = () => {
      if (!player) return;

      player.removeEventListener('play', sync);
      player.removeEventListener('pause', sync);
      player.removeEventListener('error', failed);
      player.removeEventListener('loadedmetadata', measureFrame);
      player.pause();
      player.removeAttribute('src');
      player.querySelectorAll('source').forEach(source => source.remove());
      player.load();
      player.remove();
      player = null;
    };

    const activate = position => {
      if (!open || position === index) return;

      release();
      index = position;
      paused = false;

      player = videos[index].cloneNode(true);
      player.removeAttribute('id');
      player.className = 'pps-viewer-video';
      player.controls = false;
      player.autoplay = false;
      player.loop = true;
      player.muted = muted;
      player.preload = 'metadata';
      player.setAttribute('playsinline', '');

      slides[index].appendChild(player);

      get('.pps-viewer-caption').textContent = cards[index].querySelector('figcaption span').textContent;
      get('.pps-viewer-count').textContent = `${index + 1}/${cards.length}`;

      sound.hidden = cards[index].dataset.silent === 'true';
      status.textContent = '';

      player.addEventListener('play', sync);
      player.addEventListener('pause', sync);
      player.addEventListener('error', failed);
      player.addEventListener('loadedmetadata', measureFrame);

      measureFrame();
      safePlay(player);
      sync();
    };

    function measureFrame() {
      if (!open) return;

      const width = feed.clientWidth;
      const height = feed.clientHeight;
      const ratio = player?.videoWidth / player?.videoHeight || 9 / 16;
      const mobile = matchMedia('(max-width: 749px)').matches;
      const frameWidth = mobile ? width : Math.min(width, height * ratio);

      dialog.dataset.compactFrame = String(!mobile && frameWidth < 340);
      dialog.style.setProperty('--viewer-frame-width', `${frameWidth}px`);
      dialog.style.setProperty('--viewer-frame-inset', `${mobile ? 0 : (height - Math.min(height, width / ratio)) / 2}px`);
    }

    on(window, 'resize', measureFrame);

    if ('ResizeObserver' in window) {
      const resize = new ResizeObserver(measureFrame);
      resize.observe(feed);
      removers.push(() => resize.disconnect());
    }

    const go = position => {
      if (position < 0 || position >= cards.length) return;

      feed.scrollTo({
        top: position * feed.clientHeight,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    };

    on(feed, 'scroll', () => {
      if (!open || frame) return;

      frame = requestAnimationFrame(() => {
        frame = 0;

        if (open && feed.clientHeight) {
          activate(Math.max(0, Math.min(cards.length - 1, Math.round(feed.scrollTop / feed.clientHeight))));
        }
      });
    }, { passive: true });

    on(root, 'play', event => {
      if (open && event.target.tagName === 'VIDEO') event.target.pause();
    }, true);

    const buttons = cards.map((card, position) => {
      const button = document.createElement('button');
      button.className = 'pps-reel-open';
      button.type = 'button';
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', `Open video: ${card.querySelector('figcaption span').textContent}`);
      // The full thumbnail opens the viewer; no separate expand badge.

      card.querySelector('.pps-reel-screen').appendChild(button);

      on(button, 'click', event => {
        dialog.dataset.pointerOpen = String(event.detail > 0);
        previousVideo = videos.find(video => !video.paused);

        videos.forEach(video => {
          video.pause();
          video.muted = true;
        });

        trigger = button;
        muted = true;
        oldOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        open = true;

        dialog.showModal();
        feed.scrollTo({ top: position * feed.clientHeight, behavior: 'instant' });
        activate(position);
      });

      return button;
    });

    const launch = root.querySelector('[data-pps-reels-launch]');
    if (launch) on(launch, 'click', event => {
      buttons[0].click();
      dialog.dataset.pointerOpen = String(event.detail > 0);
      trigger = launch;
    });
    on(get('.pps-viewer-close'), 'click', () => dialog.close());

    on(dialog, 'click', event => {
      if (event.target === dialog) return dialog.close();

      if (
        !event.target.classList.contains('pps-viewer-slide') ||
        !matchMedia('(min-width: 750px)').matches
      ) return;

      const bounds = event.target.getBoundingClientRect();
      const ratio = player?.videoWidth / player?.videoHeight || 9 / 16;
      const width = Math.min(bounds.width, bounds.height * ratio);
      const left = bounds.left + (bounds.width - width) / 2;

      if (event.clientX < left || event.clientX > left + width) dialog.close();
    });

    on(dialog, 'close', () => {
      open = false;
      cancelAnimationFrame(frame);
      frame = 0;
      release();
      index = -1;

      document.documentElement.style.overflow = oldOverflow;
      trigger?.focus({ preventScroll: true });

      if (
        previousVideo &&
        !document.hidden &&
        !matchMedia('(prefers-reduced-motion: reduce)').matches
      ) safePlay(previousVideo);
    });

    on(play, 'click', () => {
      if (!player) return;
      paused = !player.paused;

      if (player.paused) safePlay(player);
      else player.pause();
    });

    on(sound, 'click', () => {
      if (!player) return;
      muted = !muted;
      player.muted = muted;
      sync();
    });

    on(dialog, 'keydown', event => {
      delete dialog.dataset.pointerOpen;

      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) {
        event.preventDefault();
        go(index + (['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1));
      }
    });

    on(document, 'visibilitychange', () => {
      if (!open || !player) return;

      if (document.hidden) player.pause();
      else if (!paused) safePlay(player);
    });

    on(document, 'shopify:section:unload', event => {
      if (event.target !== root && !event.target.contains(root)) return;

      removers.forEach(remove => remove());
      cancelAnimationFrame(frame);
      release();

      if (open) document.documentElement.style.overflow = oldOverflow;

      dialog.remove();
      buttons.forEach(button => button.remove());
    });
  };

  const scan = target => {
    if (target.matches?.('[data-pps-reels]')) start(target);
    target.querySelectorAll('[data-pps-reels]').forEach(start);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
})();